import { MenuItem } from "prosemirror-menu";
import { type EditorState, NodeSelection, TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { schema } from "../adapter/schema";
import { formulaIconSpec } from "../icons";
import { isFormulaSelected, latexHasError } from "../utils";

import { TextareaField, createSelectionElement, openPopoverForm } from "./popover";

import { t } from "i18n/i18n";

export function openFormulaPopoverForm(view: EditorView, initialValue?: string): void {
  const state = view.state;
  const selectionEl = createSelectionElement(view, state.selection);

  openPopoverForm({
    appendTo: view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement,
    selectionEl,
    title: `${t("worksheet.rich_text_editor.create_formula")}:`,
    onClose: () => view.focus(),
    fields: {
      formula: new TextareaField({
        name: "formula",
        label: t("worksheet.rich_text_editor.formula_input"),
        placeholder: "e=mc^2",
        required: true,
        value: initialValue,
        validate: (value) =>
          value && latexHasError(value) ? t("validation.invalid_formula") : null,
      }),
    },
    callback(attrs) {
      const currentState = view.state;
      const formulaNode = schema.nodes.formula.create({ formula: String(attrs.formula) });
      const tr = currentState.tr.replaceSelectionWith(formulaNode);

      const pos = tr.doc.resolve(tr.selection.to);
      tr.setSelection(TextSelection.near(pos));

      view.dispatch(tr);
      view.focus();
    },
  });
}

/** Creates a toolbar MenuItem that opens the formula creation popover. */
export function formulaItem(): MenuItem {
  return new MenuItem({
    title: t("worksheet.rich_text_editor.create_formula"),
    icon: formulaIconSpec,
    active(state) {
      return isFormulaSelected(state);
    },
    run(state: EditorState, dispatch, view: EditorView) {
      if (isFormulaSelected(state)) {
        if (!dispatch) return;
        const selection = state.selection as NodeSelection;
        dispatch(
          state.tr
            .delete(selection.from, selection.from + selection.node.nodeSize)
            .scrollIntoView(),
        );
        view.focus();
        return;
      }

      if (!state.selection.empty) {
        const selectedText = state.doc.textBetween(state.selection.from, state.selection.to);
        if (selectedText.trim()) {
          if (!latexHasError(selectedText)) {
            if (!dispatch) return;
            const formulaNode = schema.nodes.formula.create({ formula: selectedText });
            const tr = state.tr.replaceSelectionWith(formulaNode).scrollIntoView();

            const pos = tr.doc.resolve(tr.selection.to);
            tr.setSelection(TextSelection.near(pos));

            dispatch(tr);
            view.focus();
            return;
          }

          openFormulaPopoverForm(view, selectedText);
          return;
        }
      }

      openFormulaPopoverForm(view);
    },
  });
}
